import { useEffect } from "react";

export interface ShortcutHandlers {
  undo(): void;
  redo(): void;
  openPalette(): void;
  focusQuickAdd(): void;
  focusFilter(): void;
  selectNext(): void;
  selectPrevious(): void;
  toggleSelected(): void;
  closeDetail(): void;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export type ShortcutAction = keyof ShortcutHandlers | undefined;

export function actionForKey(event: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey">, typing: boolean): ShortcutAction {
  const modifier = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  if (modifier && key === "k") return "openPalette";
  if (modifier && key === "z") return event.shiftKey ? "redo" : "undo";
  if (modifier && key === "y") return "redo";
  if (modifier || event.altKey || typing) return undefined;
  switch (event.key) {
    case "q":
    case "n":
      return "focusQuickAdd";
    case "/":
      return "focusFilter";
    case "j":
    case "ArrowDown":
      return "selectNext";
    case "k":
    case "ArrowUp":
      return "selectPrevious";
    case "x":
    case "e":
      return "toggleSelected";
    case "Escape":
      return "closeDetail";
    default:
      return undefined;
  }
}

/**
 * Ctrl+Z and Ctrl+Shift+Z undo and redo the task history, except while a text field is focused,
 * where they keep their native meaning for the text being typed.
 */
export function useGlobalShortcuts(handlers: ShortcutHandlers): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      const action = actionForKey(event, typing);
      if (action === undefined) return;
      const modifier = event.ctrlKey || event.metaKey;
      if (typing && modifier && (action === "undo" || action === "redo")) return;
      event.preventDefault();
      handlers[action]();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handlers]);
}
